import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import cn from 'classnames';

import { ReactComponent as AngleUpIcon } from 'assets/angle-up.svg';
import cautionIcon from 'assets/caution-icon.svg';
import chevronDownSmall from 'assets/chevron-down-small.svg';
import crossIcon from 'assets/cross.svg';
import { ReactComponent as DataContext } from 'assets/data-context.svg';
import deeperInsightsIcon from 'assets/deeperInsightsColored.svg';
import documentIcon from 'assets/document.svg';
import essentialIcon from 'assets/essential-icon.svg';
import newWindow from 'assets/new-window.svg';
import positiveIcon from 'assets/positive-icon.svg';

import { ToolTip } from 'Components/Tooltip';

import { DeeperInsightsDetails } from 'Pages/TeamDashboard/CreateReview/components/DeeperInsightsCard';
import { JourneyMapCard } from 'Pages/TeamDashboard/CreateReview/components/JourneyMapCard';
import LikeAndDismiss from 'Pages/TeamDashboard/CreateReview/components/LikeAndDismiss';
import { PositiveFinding } from 'Pages/TeamDashboard/CreateReview/components/PositiveFinding';

import { DESIGN_REMIX_STATUS, FEEDBACK_CATEGORIES } from 'utlis/constants';
import { POSITIVE_CATEGORIES } from 'utlis/constants';
import { LABELS } from 'utlis/constants';
import { TEMPLATE_TITLES } from 'utlis/constants';
import { getInsightRemixPreview } from 'utlis/remixVariants';

import styles from './styles.module.scss';

const CATEGORY_MAPPING = {
  error: { icon: essentialIcon, text: 'Essential', class: 'error' },
  suggestion: { icon: cautionIcon, text: 'Caution', class: 'warning' },
  positive: { icon: positiveIcon, text: 'Positive', class: 'positive' },
  'deeper-insights': { icon: deeperInsightsIcon, text: 'New Idea', class: 'deeper-insights' },
};

const TOOLTIP_COPY = {
  error: { title: 'Error', desc: 'Must fixes, such as visual issues or poor accessibility' },
  suggestion: { title: 'Recommendation', desc: 'Nice-to-have suggestions or improvements' },
  'deeper-insights': { title: 'New Idea', desc: 'Advanced strategic recommendations for maximum impact' },
  'default': { title: 'Positive', desc: 'Decisions adding value to your design' },
};

const COLLAPSE_HEIGHT_THRESHOLD = 344;

function highlightNumberContext(text) {
  // Match a word, optional whitespace, a number (with optional % or decimal), optional whitespace, a word
  const regex = /(\S+)\s+(\d+(?:\.\d+)?%?)\s+(\S+)/g;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(text)) !== null) {
    const matchStart = match.index;
    const matchEnd = match.index + match[0].length;

    // Entire match already consumed by a previous overlapping match — skip without rewinding
    if (matchEnd <= lastIndex) {
      continue;
    }

    if (matchStart > lastIndex) {
      parts.push(text.slice(lastIndex, matchStart));
    }

    // If this match overlaps with the previous match,
    // only render the part that has not already been rendered.
    const textToHighlight
      = matchStart < lastIndex
        ? text.slice(lastIndex, matchEnd)
        : match[0];

    if (textToHighlight) {
      parts.push(
        <strong key={`${matchStart}-${matchEnd}`}>
          {textToHighlight}
        </strong>
      );
    }

    lastIndex = Math.max(lastIndex, matchEnd);

    // Rewind so overlapping matches are still detected — position just before the trailing word
    // so it can serve as the leading word of the next match, regardless of whitespace width
    regex.lastIndex = matchEnd - match[3].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
}

function getFavicon(url) {
  return `http://www.google.com/s2/favicons?domain=${url}`;
}

async function fetchUrlMeta(url) {
  try {
    const res = await fetch(`https://api.microlink.io?url=${encodeURIComponent(url)}`);
    const json = await res.json();

    return {
      title: json.data?.title || null,
      favicon: json.data?.logo?.url || getFavicon(url),
      image: json.data?.image?.url || null,
    };
  }
  catch {
    return { title: null, favicon: getFavicon(url), image: null };
  }
}

export const FeedbackBox = ({
  data,
  index,
  currentFeedback,
  selectedCategory,
  selectedLabelCategory,
  onRollingReviews,
  isPublicView = false,
  onInspectFeedback,
  totalImages = 0,
  isSelected = false,
}) => {
  const currentRecord = useSelector(state => state.historyReducer.currentRecord);

  const contentRef = useRef(null);
  const fullCardRef = useRef(null);
  const resolvedSourceUrl = useMemo(
    () => data?.case_studies?.url
      || data?.guideline?.url
      || (typeof data?.guideline === 'string' ? data.guideline : null),
    [data?.case_studies?.url, data?.guideline]
  );

  const [errorMsg, setErrorMsg] = useState('');
  const [urlSourceArticle, setUrlSourceArticle] = useState('');
  const [urlSourceFavicon, setUrlSourceFavicon] = useState('');
  const [urlSourceImage, setUrlSourceImage] = useState('');
  const [isUrlSourceLoading, setIsUrlSourceLoading] = useState(!!resolvedSourceUrl);
  const [dismiss, setDismiss] = useState(data.dismiss ?? false);
  const [showFeedback, setShowFeedback] = useState(!data.dismiss ?? true);
  const [fullCardHeight, setFullCardHeight] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const isEmptyPositive = useMemo(
    () => data.category === FEEDBACK_CATEGORIES.positive && POSITIVE_CATEGORIES.every(
      ({ key }) => !(key in data) || (Array.isArray(data[key]) && data[key].length === 0)
    ),
    [data]
  );

  const category = useMemo(() => getCategory(), [data]);

  const highlightedAnalytics = useMemo(
    () => data.analytics_reasoning
      ? highlightNumberContext(data.analytics_reasoning)
      : null,
    [data.analytics_reasoning]
  );

  const severityClass = useMemo(
    () => category && CATEGORY_MAPPING[category]
      ? styles[CATEGORY_MAPPING[category].class]
      : '',
    [category]
  );

  const cardCategoryClass = useMemo(
    () => category && CATEGORY_MAPPING[category]
      ? styles[`card-${CATEGORY_MAPPING[category].class}`]
      : '',
    [category]
  );

  const showSeeMoreButton = fullCardHeight == null || fullCardHeight >= COLLAPSE_HEIGHT_THRESHOLD;
  const containerHeight = undefined;
  const isCardCollapsed = showSeeMoreButton && !isExpanded;

  // Determine if card is inspectable (click navigates to design image)
  const isCardInspectable = useMemo(() => {
    // Don't show if no page is assigned
    if (!data.page) {
      return false;
    }

    // Don't show if page is out of range
    if (totalImages > 0) {
      const pageIndex = data.page - 1; // Convert to 0-based index

      if (pageIndex < 0 || pageIndex >= totalImages) {
        return false;
      }
    }

    return true;
  }, [data.page, totalImages]);

  useEffect(() => {
    if (resolvedSourceUrl) {
      setIsUrlSourceLoading(true);
      fetchUrlMeta(resolvedSourceUrl).then(({ title, favicon, image }) => {
        setUrlSourceArticle(title || resolvedSourceUrl);
        setUrlSourceFavicon(favicon);
        setUrlSourceImage(image);
        setIsUrlSourceLoading(false);
      });
    }
  }, [resolvedSourceUrl]);

  // Update dismiss state when data changes
  useEffect(() => {
    setDismiss(data.dismiss ?? false);
    setShowFeedback(!data.dismiss ?? true);
  }, [data.dismiss]);

  // A dismissed card is collapsed (its prototype panel is not mounted). While a prototype or a variant is being
  // generated for it (e.g. from the Ask Audit chat) open it, so the panel can poll and show the generation.
  const remixPreview = data.isDeeperInsight ? getInsightRemixPreview(currentRecord, data.uuid) : null;
  const isRemixInProgress = remixPreview?.status === DESIGN_REMIX_STATUS.IN_PROGRESS
    || Boolean(remixPreview?.refinements?.some?.(refinement => refinement?.status === DESIGN_REMIX_STATUS.IN_PROGRESS));

  useEffect(() => {
    if (isRemixInProgress) {
      setShowFeedback(true);
    }
  }, [isRemixInProgress]);

  // Measure heights for progressive disclosure (decision based on full card >= 344px)
  useEffect(() => {
    const fullEl = fullCardRef.current;
    const bodyEl = contentRef.current;

    if (!fullEl || !bodyEl) {
      return;
    }

    const measure = () => {
      // Hidden (the Findings list is hidden on the Ask audit tab): there is nothing to measure, and a height
      // of 0 would show the card expanded until the next measure when the tab is shown again
      if (fullEl.getClientRects().length === 0) {
        return;
      }

      // Full card height (top bar + body + footer) – used to decide "See more"
      setFullCardHeight(fullEl.scrollHeight);
    };

    requestAnimationFrame(() => {
      measure();
    });

    // ResizeObserver not supported in IE11; progressive disclosure targets modern browsers
    // eslint-disable-next-line compat/compat
    const ro = new ResizeObserver(measure);

    ro.observe(fullEl);

    ro.observe(bodyEl);

    return () => {
      ro.disconnect();
    };
  }, [data, showFeedback]);

  // Reset expanded state only when switching to a different feedback item (not on like/dislike or design selection)
  useEffect(() => {
    setIsExpanded(false);
  }, [data?.uuid]);

  // Hide dismissed feedback completely on public view
  if (isPublicView && data.dismiss) {
    return null;
  }

  if (isEmptyPositive && !data.psychology_insight) {
    return;
  }

  if (
    (currentRecord && currentRecord.extra_data?.template === TEMPLATE_TITLES.USER_JOURNEYS)
  ) {
    return (
      <JourneyMapCard data={data} onRollingReviews={onRollingReviews} />
    );
  }

  return (
    <div
      className={cn(
        styles['feedback-box'],
        cardCategoryClass,
        {
          [styles['hide']]: !(selectedLabelCategory === 'All themes'
          || selectedLabelCategory === data.label?.toLowerCase()
          ),
          [styles['padding-0']]: dismiss && !showFeedback,
          [styles['padding-top-0']]: dismiss && showFeedback,
          [styles['selected']]: isSelected,
        }
      )}
    >
      {dismiss && (
        <button
          className={cn(
            styles['dismiss-heading-container'],
            { [styles['border-bottom-none']]: !showFeedback }
          )}
          onClick={() => setShowFeedback(!showFeedback)}
        >
          <h3 >
            This finding has been dismissed
          </h3>
          <AngleUpIcon className={cn(styles['angle-up-icon'], { [styles['rotate']]: !showFeedback })} />
        </button>
      )}
      {showFeedback && (
        <div
          ref={fullCardRef}
          role={isCardInspectable ? 'button' : undefined}
          tabIndex={isCardInspectable ? 0 : undefined}
          className={cn(
            styles['full-card-wrapper'],
            { [styles['full-card-collapsed']]: isCardCollapsed },
            { [styles['clickable-card']]: isCardInspectable }
          )}
          onClick={isCardInspectable ? handleCardClick : undefined}
        >
          <div className={styles['box-header']}>
            <div className={styles['tag-container']}>
              {category && CATEGORY_MAPPING[category] && (
                <ToolTip
                  title={toolTipTitle(category)}
                  position="bottom"
                  width="160px"
                  className={cn(
                    styles['tooltip-position'],
                    { [styles['deeper-insights-tooltip']]: data.isDeeperInsight }
                  )}
                  textContainerClassName={styles['tooltip-content']}
                >
                  <div className={cn(styles['severity-tag'], severityClass)}>
                    <img
                      src={CATEGORY_MAPPING[category].icon}
                      alt="icon"
                    />
                    <span className={styles['finding-index']}>
                      #
                      {' '}
                      {index}
                    </span>
                  </div>
                </ToolTip>
              )}
            </div>
            {(data.label || data.isDeeperInsight || data.category === 'deeper_insight') && (
              <div className={styles['label-tag']}>
                <span className={styles['label-tag-text']}>
                  {data.isDeeperInsight || data.category === 'deeper_insight' ? 'New idea' : data.label}
                </span>
              </div>
            )}
          </div>
          <div
            ref={contentRef}
            className={cn(
              styles['feedback-box-container'],
              {
                [styles['collapsible']]: showSeeMoreButton,
                [styles['body-collapsed']]: isCardCollapsed,
                [styles['gradient']]: isCardCollapsed,
              }
            )}
            style={containerHeight != null ? { height: containerHeight } : undefined}
          >
            <div className={styles['heading-count']}>
              <h2>
                {data.reason && data.detailed_feedback
                  ? renderHighlightedFeedback(data.detailed_feedback, data.feedback_highlight)
                  : data.issue || 'Positive findings in your design'}
              </h2>
            </div>
            {!data.reason && data.detailed_feedback && !data.isDeeperInsight && data.detailed_feedback.map(
              val => (
                <p className={styles['list']} key={val}>
                  •
                  {' '}
                  {val}
                </p>
              )
            )}
            {data.isDeeperInsight && (
              <DeeperInsightsDetails
                data={data}
                insightUuid={data.uuid}
                currentFeedbackUuid={currentRecord?.uuid}
                isPublicView={isPublicView}
                isCardExpanded={isExpanded}
              />
            )}
            {data.category === FEEDBACK_CATEGORIES.positive && !data.psychology_insight
              && (selectedCategory === 'none'
              || normalizeCategoryKey(selectedCategory) === data?.positiveCategory) && (
              <PositiveFinding data={data} selectedCategory={selectedCategory} />
            )}
            {data.analytics_reasoning
              ? (
                <div className={styles['reason-column']} >
                  <p className={cn(styles['data-context-text'], styles['psychology-text'])}>
                    <DataContext />
                    {' '}
                    Data context
                  </p>
                  <p className={styles['list']}>
                    {highlightedAnalytics}
                  </p>
                </div>
              )
              : data.psychology_principle && data.psychology_insight && (
                <div className={styles['reason-column']} >
                  <p className={styles['psychology-text']}>
                    {data.psychology_principle}
                  </p>
                  <p className={styles['list']}>
                    {data.psychology_insight}
                  </p>
                </div>
              )}
            {data?.edge_cases && data.edge_cases.length > 0 && (
              <div className={styles['column']} >
                {data.edge_cases.map((item, ind) => (
                  <div className={styles['feedback']} key={ind}>
                    <div className={styles['cross-icon-container']}>
                      <img src={crossIcon} />
                    </div>
                    <div>
                      <p className={styles['bold-minor-heading']}>
                        {item.scenario}
                      </p>
                      <p className={styles['list']}>
                        {item.explanation}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {(data.reason
              && (!data.label || data.label?.toLowerCase() !== LABELS.ACCESSIBILITY.toLowerCase()))
              && (
                <div className={styles['reason-column']} >
                  <p className={styles['sub-heading']}>
                    Why
                  </p>
                  <p className={styles['list']}>
                    {data.reason}
                  </p>
                </div>
              )}
            {data.case_studies && (
              <div className={styles['case-study-row']}>
                <p className={styles['case-study']}>
                  {typeof data.case_studies === 'string' ? data.case_studies : data.case_studies.description}
                </p>
                {isUrlSourceLoading
                  ? <div className={styles['skeleton-image']} />
                  : urlSourceImage && (
                    <img
                      src={urlSourceImage}
                      alt="article preview"
                      className={styles['case-study-preview']}
                    />
                  )}
              </div>
            )}
            {data?.brand_source && (
              <div className={styles['column']}>
                <p className={styles['sub-heading']}>
                  Source
                </p>
                <div className={styles['bar']}>
                  <img src={documentIcon} alt="icon" />
                  <p className={styles['article-name']}>
                    {data.brand_source}
                  </p>
                </div>
              </div>
            )}
            {resolvedSourceUrl && (isUrlSourceLoading || urlSourceArticle || urlSourceFavicon) && (
              <div className={styles['column']}>
                <p className={styles['sub-heading']}>
                  Source
                </p>
                {isUrlSourceLoading
                  ? <div className={styles['skeleton-bar']} />
                  : (
                    <div
                      className={styles['bar']}
                      onClick={handleCaseStudyClick}
                    >
                      {urlSourceFavicon && <img src={urlSourceFavicon} alt="icon" />}
                      <p className={styles['article-name']}>
                        {urlSourceArticle}
                      </p>
                      <img src={newWindow} className={styles['open-icon']} alt="open-link" />
                    </div>
                  )}
              </div>
            )}
            {
              errorMsg && (
                <p className={styles['design-error']}>
                  {errorMsg}
                </p>
              )
            }
          </div>
          {showSeeMoreButton && (
            <div className={styles['see-more-wrapper']}>
              <button
                type="button"
                onClick={() => setIsExpanded(!isExpanded)}
                className={styles['see-more-button']}
              >
                <img
                  src={chevronDownSmall}
                  alt="expand-collapse"
                  className={cn(styles['see-more-chevron'], { [styles['point-up']]: isExpanded })}
                />
                <span>
                  {isExpanded ? 'See less' : 'See more'}
                </span>
              </button>
            </div>
          )}
          {!onRollingReviews && !isPublicView && (
            <div className={styles['full-card-footer']}>
              <LikeAndDismiss
                dismiss={dismiss}
                currentFeedback={currentFeedback}
                data={data}
                setErrorMsg={setErrorMsg}
                setShowFeedback={setShowFeedback}
                setDismiss={setDismiss}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );

  function renderHighlightedFeedback(detailedFeedback, highlightText) {
    const text = typeof detailedFeedback === 'string'
      ? detailedFeedback
      : detailedFeedback?.[0];

    if (!highlightText || !text.includes(highlightText)) {
      return text ?? '';
    }

    const startIndex = text.indexOf(highlightText);
    const beforeHighlight = text.slice(0, startIndex);
    const afterHighlight = text.slice(startIndex + highlightText.length);

    return (
      <>
        {beforeHighlight}
        <span className={styles['feedback-highlight']}>
          {highlightText}
        </span>
        {afterHighlight}
      </>
    );
  }

  function getCategory() {
    if (data.isDeeperInsight) {
      return 'deeper-insights';
    }

    return data.category || '';
  }

  function normalizeCategoryKey(categoryKey) {
    const categoryMap = {
      LAYOUT: 'layoutAndHierarchy',
      TYPOGRAPHY: 'typography',
      COLOR: 'color',
    };

    return categoryMap[categoryKey?.toUpperCase()] || categoryKey;
  }

  function goToCaseStudy() {
    if (resolvedSourceUrl) {
      window.open(resolvedSourceUrl, '_blank');
    }
  }

  function handleCaseStudyClick(e) {
    e.stopPropagation();
    goToCaseStudy();
  }

  function handleInspectClick() {
    if (onInspectFeedback && data.page) {
      onInspectFeedback(data);
    }
    else {
      // eslint-disable-next-line no-console
      console.warn('Cannot inspect: missing callback or page data', {
        hasCallback: !!onInspectFeedback,
        hasPage: !!data.page,
        data,
      });
    }
  }

  function handleCardClick() {
    if (isCardInspectable) {
      handleInspectClick();
    }
  }

  function toolTipTitle(categoryType) {
    let item = TOOLTIP_COPY[categoryType];

    if (!item) {
      item = TOOLTIP_COPY['default'];
    }

    return (
      <div>
        {item.desc}
      </div>
    );
  }
};

FeedbackBox.propTypes = {
  data: PropTypes.object,
  index: PropTypes.number,
  currentFeedback: PropTypes.number,
  selectedCategory: PropTypes.string,
  selectedLabelCategory: PropTypes.string,
  onRollingReviews: PropTypes.bool,
  isPublicView: PropTypes.bool,
  onInspectFeedback: PropTypes.func,
  totalImages: PropTypes.number,
  isSelected: PropTypes.bool,
};

